import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { notify } from "../../components/ui/notify";
import {
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Button,
  Card,
  Descriptions,
  Divider,
  Modal,
  Image,
  Tooltip,
  Select,
  InputNumber,
  Upload,
  Popconfirm,
  Input,
} from "antd";
import ScrollDatePicker from "../../components/calendar/ScrollDatePicker";
import {
  EditOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  UserOutlined,
  ScissorOutlined,
  BankOutlined,
  LoadingOutlined,
  PlusOutlined,
  TrophyOutlined,
  LinkOutlined,
  ExportOutlined,
  UploadOutlined,
  FileOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import axios from "axios";

import { fmtDate, fmtDateTime } from "../../components/conciliacion/detalles/laboralExpedienteFormatters";
import {
  actionDesvinculacionGetById,
  actionAuditLogGet,
  actionDesvinculacionDeleteById,
  actionDesvinculacionUpdateById,
  actionDesvinculacionPagosGet,
  actionDesvinculacionPagoCreate,
  actionDesvinculacionPagoUpdate,
  actionDesvinculacionPagoDelete,
  actionDesvinculacionDocumentoCreate,
  actionDesvinculacionDocumentoDelete,
  actionDesvinculacionEmpleadoCreate,
  actionDesvinculacionEmpleadoUpdate,
  actionDesvinculacionEmpleadoDelete,
} from "../../redux/actions/desvinculaciones/desvinculaciones";
import { actionConciliacionGetByIDNoRedux } from "../../redux/actions/conciliacion/conciliacion";
import { PATH } from "../../redux/utils";
import useLaboralCatalogos from "./materias/laboral/useLaboralCatalogos";
import { DataBadge } from "../../components/ui";
import { STATUS_PILL_HEX } from "./materias/laboral/tribunalEstatus";
import "../../components/conciliacion/detalles/LaboralExpedienteDetallePage.css";

const API_BASE = PATH;
const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const { Title, Text } = Typography;

/* ─── helpers ─────────────────────────────────────────────────── */
const toTitleCase = (str) => {
  if (!str || typeof str !== "string") return "—";
  return str.toLowerCase().split(" ").filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

const safeText = (v) => {
  if (v === undefined || v === null) return "—";
  const s = String(v).trim();
  return s || "—";
};

const normalize = (str) =>
  String(str || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();

const getFechaBajaColor = (v) => {
  if (!v) return undefined;
  const d = dayjs(v);
  return d.isValid() ? (d.isAfter(dayjs()) ? "#16a34a" : "#dc2626") : undefined;
};

const MODALIDAD_LABEL = { privado: "Privada", ratificado: "Ratificada" };
const LUGAR_LABEL = {
  centro_trabajo: "Centro de trabajo",
  despacho: "Despacho",
  centro_conciliacion: "Centro de Conciliación",
};

// Opciones de resultado se cargan dinámicamente desde la API
const esResultadoConvenio = (label = "") =>
  label.toLowerCase().includes("soluciona") || label.toLowerCase().includes("éxito") || label.toLowerCase().includes("exito");
const esResultadoCitatorio = (label = "") =>
  label.toLowerCase().includes("citatorio") || label.toLowerCase().includes("espera");

const METODO_PAGO_OPTIONS = [
  { value: "1", label: "Efectivo" },
  { value: "2", label: "Transferencia" },
  { value: "3", label: "Cheque" },
  { value: "4", label: "Orden de Pago" },
];

const TIPO_PAGO_OPTIONS = [
  { value: "inmediato", label: "Inmediato" },
  { value: "diferido", label: "Diferido" },
];

const computeStatus = (detalle) => {
  const raw = normalize(detalle?.status_nombre || detalle?.status || detalle?.estatus || "");
  if (raw.includes("solucion") || raw.includes("exito"))
    return { label: "Solucionada con éxito", color: "success" };
  if (raw.includes("proceso"))
    return { label: "En proceso", color: "warning" };
  if (raw.includes("citatorio") || raw.includes("prejudicial") || raw.includes("espera"))
    return { label: "En espera de citatorio", color: "error" };
  if (raw.includes("agendad"))
    return { label: "Agendada", color: "default" };
  return { label: raw ? toTitleCase(raw) : "Registrada", color: "default" };
};

/* ─── field grid components ──────────────────────────────────── */

function FieldItem({ label, value, isTag, tagColor, span, valueColor }) {
  return (
    <div style={{ gridColumn: span === 2 ? "1 / -1" : undefined }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#9ca3af", marginBottom: 4, letterSpacing: "0.04em" }}>
        {label}
      </div>
      {isTag ? (
        <Tag color={tagColor} style={{ marginTop: 2 }}>{value}</Tag>
      ) : (
        <div style={{ fontSize: 14, fontWeight: 600, color: valueColor || "#111827", wordBreak: "break-word" }}>
          {value || "—"}
        </div>
      )}
    </div>
  );
}

function IconCard({ icon, iconBg, iconColor, title, extra, children, style }) {
  return (
    <Card
      style={{ borderRadius: 12, border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,.06)", height: "100%", ...style }}
      bodyStyle={{ padding: 24 }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ background: iconBg, borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {React.cloneElement(icon, { style: { fontSize: 18, color: iconColor } })}
          </div>
          <Text strong style={{ fontSize: 16, color: "#111827" }}>{title}</Text>
        </div>
        {extra}
      </div>
      {children}
    </Card>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <section className="laboral-exp-section">
      <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2">
        <div className="laboral-exp-section-header">
          <div>
            <Text className="laboral-exp-section-title" style={{ fontSize: 18 }}>{title}</Text>
            {subtitle && (
              <Text type="secondary" className="laboral-exp-section-sub">{subtitle}</Text>
            )}
          </div>
        </div>
        <Divider className="laboral-exp-section-divider" />
        {children}
      </Card>
    </section>
  );
}

/* ─── page ──────────────────────────────────────────────────── */
export default function LaboralDesvinculacionesDetallePage({ maps = {} }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { idExpediente } = useParams();

  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [deletingExp, setDeletingExp] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [resultadoBaja, setResultadoBaja] = useState(null); // id_desvinculacion_resultado
  const [resultadoSaving, setResultadoSaving] = useState(false);
  const [resultadosOptions, setResultadosOptions] = useState([]); // { value, label }
  const [pagosConvenio, setPagosConvenio] = useState([]);
  const [pagosSaving, setPagosSaving] = useState(false);
  const [pagoEditMode, setPagoEditMode] = useState(false);
  const originalPagoIds = useRef(new Set());
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [vinculoConciliacion, setVinculoConciliacion] = useState(null);
  const [loadingVinculo, setLoadingVinculo] = useState(false);
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const [docFileList, setDocFileList] = useState([]);

  // ===== Edición inline: tarjeta "Datos de la desvinculación" =====
  const [editingDatosCard, setEditingDatosCard] = useState(false);
  const [editingDatosForm, setEditingDatosForm] = useState({});
  const [savingDatosCard, setSavingDatosCard] = useState(false);
  const [selectedEstadoId, setSelectedEstadoId] = useState(null);
  const [selectedCiudadId, setSelectedCiudadId] = useState(null);
  const [modalidadBajaOptions, setModalidadBajaOptions] = useState([]);
  const [lugarEjecucionOptions, setLugarEjecucionOptions] = useState([]);

  const {
    estadosOptions,
    ciudadesOptions: ciudadesOptionsCatalogo,
    ciudadesById: ciudadesByIdCatalogo,
  } = useLaboralCatalogos(
    selectedEstadoId,
    selectedCiudadId,
    { modulo: "desvinculaciones" }
  );

  // ===== Edición inline: tarjeta "Empleados" =====
  const [editingEmpleadosCard, setEditingEmpleadosCard] = useState(false);
  const [empleadosEdit, setEmpleadosEdit] = useState([]);
  const [savingEmpleadosCard, setSavingEmpleadosCard] = useState(false);
  const originalEmpleadoIds = useRef(new Set());

  const empresasSlice = useSelector((state) => state?.empresas ?? {});
  const empresasItems = useMemo(() => {
    const slice = empresasSlice;
    if (Array.isArray(slice)) return slice;
    if (Array.isArray(slice?.items)) return slice.items;
    if (Array.isArray(slice?.data)) return slice.data;
    if (Array.isArray(slice?.data?.items)) return slice.data.items;
    return [];
  }, [empresasSlice]);

  useEffect(() => {
    (async () => {
      try {
        const [mRes, lRes] = await Promise.all([
          axios.get(`${API_BASE}catalogos/modalidad_baja`, { headers: authHeader() }),
          axios.get(`${API_BASE}catalogos/lugar_ejecucion`, { headers: authHeader() }),
        ]);
        const mData = Array.isArray(mRes.data) ? mRes.data : (mRes.data?.data ?? []);
        const lData = Array.isArray(lRes.data) ? lRes.data : (lRes.data?.data ?? []);
        setModalidadBajaOptions(
          mData.filter((d) => d.active == null || d.active).map((d) => ({ value: d.id_modalidad_baja, label: d.nombre }))
        );
        setLugarEjecucionOptions(
          lData.filter((d) => d.active == null || d.active).map((d) => ({ value: d.id_lugar_ejecucion, label: d.nombre }))
        );
      } catch {
        setModalidadBajaOptions([]);
        setLugarEjecucionOptions([]);
      }
    })();
  }, []);

  const lugarOtroId = useMemo(() => {
    const opt = lugarEjecucionOptions.find((o) => (o.label || "").toLowerCase() === "otro");
    return opt?.value ?? null;
  }, [lugarEjecucionOptions]);

  const razonesSocialesOptions = useMemo(() => {
    const map = new Map();
    empresasItems.forEach((it) => {
      const rsArr = Array.isArray(it?.razones_sociales) ? it.razones_sociales : [];
      rsArr.forEach((rs) => {
        const label = rs?.nombre ?? rs?.razon_social ?? "";
        const value = rs?.id_empresa_razon_social ?? rs?.id ?? null;
        if (label && value != null && !map.has(value)) map.set(value, { label, value });
      });
    });
    // Respaldo: garantiza que la razón social ya vinculada a esta
    // desvinculación siempre tenga una etiqueta legible, aunque el
    // catálogo de empresas aún no haya cargado.
    if (detalle?.id_razon_social != null && detalle?.nombre_razon_social) {
      if (!map.has(detalle.id_razon_social)) {
        map.set(detalle.id_razon_social, { label: detalle.nombre_razon_social, value: detalle.id_razon_social });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "es"));
  }, [empresasItems, detalle?.id_razon_social, detalle?.nombre_razon_social]);

  const { ciudadesById = {}, estadosById = {} } = maps;

  /* ── catálogo de resultados (hardcoded según tabla desvinculaciones_resultados) ── */
  useEffect(() => {
    setResultadosOptions([
      { value: 1, label: "Solucionada con éxito" },
      { value: 2, label: "En espera de citatorio prejudicial" },
    ]);
  }, []);

  /* ── data load ── */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingDetalle(true);
        const data = await dispatch(actionDesvinculacionGetById(idExpediente));
        if (!mounted) return;
        setDetalle(data || null);
      } catch (err) {
        if (!mounted) return;
        notify.error({ message: "Error al cargar", description: "No se pudo obtener el detalle." });
        setDetalle(null);
      } finally {
        if (mounted) setLoadingDetalle(false);
      }
    })();
    return () => { mounted = false; };
  }, [dispatch, idExpediente]);

  useEffect(() => {
    if (!detalle?.id) return;
    dispatch(actionAuditLogGet({ id_modulo: 2, id_key: detalle.id, limit: 100 }));
  }, [dispatch, detalle?.id]);

  useEffect(() => {
    if (!detalle) return;
    setResultadoBaja(detalle.id_desvinculacion_resultado ?? null);
  }, [detalle?.id]);

  useEffect(() => {
    if (!detalle?.id) return;
    let mounted = true;
    (async () => {
      try {
        const data = await dispatch(actionDesvinculacionPagosGet(detalle.id));
        if (!mounted) return;
        const items = Array.isArray(data) ? data : (data?.items ?? []);
        originalPagoIds.current = new Set(items.map((p) => p.id_desvinculacion_convenio));
        setPagosConvenio(items.map((p, i) => ({
          _key: `loaded_${detalle.id}_${i}`,
          id: p.id_desvinculacion_convenio,
          monto: p.monto,
          fecha: p.fecha_pago ? dayjs(p.fecha_pago) : null,
          forma_pago: p.metodo_pago,
          tipo_pago: p.tipo_pago,
        })));
      } catch {
        if (mounted) setPagosConvenio([]);
      }
    })();
    return () => { mounted = false; };
  }, [detalle?.id, dispatch]);

  // Cargar vínculo con Centro de Conciliación cuando carga el detalle
  useEffect(() => {
    if (!detalle?.id) return;

    const idFromBackend = detalle.id_conciliacion_vinculada;
    const idFromStorage = (() => {
      try {
        const s = localStorage.getItem(`desvinc_vinculo_${detalle.id}`);
        return s ? parseInt(s, 10) : null;
      } catch { return null; }
    })();

    const vinculoId = idFromBackend || idFromStorage;
    if (!vinculoId) return;

    setLoadingVinculo(true);
    dispatch(actionConciliacionGetByIDNoRedux(
      vinculoId,
      (data) => { setVinculoConciliacion(data); setLoadingVinculo(false); },
      () => { setLoadingVinculo(false); }
    ));
  }, [detalle?.id, detalle?.id_conciliacion_vinculada, dispatch]);

  // Detectar navegación de regreso desde la creación del expediente vinculado
  useEffect(() => {
    const linkedId = location.state?.linkedConciliacionId;
    if (!linkedId || !detalle?.id) return;

    // Persistir en localStorage (no se usa PUT parcial al backend para no riesgar borrar resultado_baja)
    try { localStorage.setItem(`desvinc_vinculo_${detalle.id}`, String(linkedId)); } catch {}

    // Cargar datos del expediente vinculado
    setLoadingVinculo(true);
    dispatch(actionConciliacionGetByIDNoRedux(
      linkedId,
      (data) => { setVinculoConciliacion(data); setLoadingVinculo(false); },
      () => { setLoadingVinculo(false); }
    ));

    // Limpiar state de navegación para evitar re-ejecución
    navigate(location.pathname, { replace: true, state: null });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.linkedConciliacionId, detalle?.id]);

  /* ── derived data ── */
  const ciudadName = useMemo(() => {
    if (!detalle) return "—";
    return detalle.nombre_ciudad || ciudadesById[detalle.id_ciudad]?.nombre || "—";
  }, [detalle, ciudadesById]);

  const estadoName = useMemo(() => {
    if (!detalle) return "—";
    if (detalle.nombre_estado) return detalle.nombre_estado;
    const ciudad = ciudadesById[detalle.id_ciudad];
    if (ciudad?.id_estado && estadosById[ciudad.id_estado]) {
      return estadosById[ciudad.id_estado].nombre || "—";
    }
    return "—";
  }, [detalle, ciudadesById, estadosById]);

  const status = useMemo(() => (detalle ? computeStatus(detalle) : { label: "—", color: "default" }), [detalle]);


  const handleResultadoChange = async (value) => {
    if (!detalle?.id || resultadoSaving) return;
    const prev = resultadoBaja;
    setResultadoBaja(value ?? null);
    setResultadoSaving(true);
    try {
      const fechaResultado = value ? dayjs().format("YYYY-MM-DD HH:mm:ss") : null;
      await dispatch(actionDesvinculacionUpdateById(detalle.id, {
        id_desvinculacion_resultado: value ?? null,
        fecha_resultado: fechaResultado,
      }));
      setDetalle((d) => ({ ...d, id_desvinculacion_resultado: value ?? null, fecha_resultado: fechaResultado }));
      notify.success({ message: "Resultado actualizado", placement: "bottomRight", duration: 2 });
      const label = resultadosOptions.find((o) => o.value === value)?.label ?? "";
      if (esResultadoConvenio(label)) setActiveTab("4");
      if (esResultadoCitatorio(label) && !vinculoConciliacion) setShowLinkDialog(true);
    } catch {
      setResultadoBaja(prev);
      notify.error({ message: "No se pudo actualizar el resultado", placement: "bottomRight", duration: 4 });
    } finally {
      setResultadoSaving(false);
    }
  };

  const handleCreateLinkedExpediente = () => {
    setShowLinkDialog(false);
    navigate("/materias/laboral/centro-conciliacion/crear", {
      state: {
        fromDesvinculacion: detalle.id,
        idCiudad: detalle.id_ciudad ?? null,
        prefill: {
          trabajador_nombre: empleadosList.join(", ") || detalle.nombre_solicitante || detalle.nombre_trabajador || "",
          id_razon_social: detalle.id_razon_social ?? null,
        },
      },
    });
  };

  const addPago = () => {
    setPagosConvenio((prev) => [
      ...prev,
      { _key: `p_${Date.now()}`, monto: null, fecha: null, forma_pago: undefined, tipo_pago: undefined },
    ]);
  };

  const removePago = (key) => setPagosConvenio((prev) => prev.filter((p) => p._key !== key));

  const updatePago = (key, field, value) =>
    setPagosConvenio((prev) => prev.map((p) => (p._key === key ? { ...p, [field]: value } : p)));

  const handleTipoPagoChange = (key, value) => {
    if (value === "inmediato") {
      const fechaBaja = detalle?.fecha_solicitacion ? dayjs(detalle.fecha_solicitacion) : null;
      setPagosConvenio((prev) =>
        prev.map((p) => p._key === key ? { ...p, tipo_pago: value, fecha: fechaBaja } : p)
      );
    } else {
      updatePago(key, "tipo_pago", value);
    }
  };

  const handleSavePagos = async () => {
    if (!detalle?.id || pagosSaving) return;
    setPagosSaving(true);
    try {
      const currentIds = new Set(pagosConvenio.filter((p) => p.id).map((p) => p.id));

      // Eliminar los que se quitaron de la lista
      const toDelete = [...originalPagoIds.current].filter((id) => !currentIds.has(id));
      await Promise.all(toDelete.map((id) => dispatch(actionDesvinculacionPagoDelete(id))));

      // Crear o actualizar cada pago
      const saved = await Promise.all(pagosConvenio.map(async (p) => {
        const payload = {
          id_desvinculacion: detalle.id,
          fecha_pago: p.fecha ? dayjs(p.fecha).format("YYYY-MM-DD HH:mm:ss") : null,
          metodo_pago: p.forma_pago ?? null,
          tipo_pago: p.tipo_pago ?? null,
          monto: p.monto != null ? String(p.monto) : null,
        };
        if (p.id) {
          await dispatch(actionDesvinculacionPagoUpdate(p.id, payload));
          return p;
        } else {
          const res = await dispatch(actionDesvinculacionPagoCreate(payload));
          return { ...p, id: res?.id_desvinculacion_convenio ?? res?.id ?? null };
        }
      }));

      originalPagoIds.current = new Set(saved.filter((p) => p.id).map((p) => p.id));
      setPagosConvenio(saved);
      setPagoEditMode(false);
      notify.success({ message: "Pagos guardados", placement: "bottomRight", duration: 2 });
    } catch {
      notify.error({ message: "No se pudieron guardar los pagos", placement: "bottomRight" });
    } finally {
      setPagosSaving(false);
    }
  };

  const startEditEmpleadosCard = () => {
    const arr = Array.isArray(detalle?.empleados) ? detalle.empleados : [];
    const rows = arr.map((e, i) => ({
      _key: `emp_${e?.id ?? i}`,
      id: e?.id ?? null,
      nombre: String(e?.nombre_completo ?? e?.nombre_empleado ?? "").trim(),
    }));
    originalEmpleadoIds.current = new Set(rows.filter((r) => r.id).map((r) => r.id));
    setEmpleadosEdit(rows);
    setEditingEmpleadosCard(true);
  };

  const handleCancelEditEmpleados = () => setEditingEmpleadosCard(false);

  const addEmpleadoRow = () => {
    setEmpleadosEdit((prev) => [...prev, { _key: `emp_new_${Date.now()}`, id: null, nombre: "" }]);
  };

  const removeEmpleadoRow = (key) =>
    setEmpleadosEdit((prev) => (prev.length > 1 ? prev.filter((r) => r._key !== key) : prev));

  const updateEmpleadoRow = (key, nombre) =>
    setEmpleadosEdit((prev) => prev.map((r) => (r._key === key ? { ...r, nombre } : r)));

  const handleSaveEmpleados = async () => {
    if (!detalle?.id || savingEmpleadosCard) return;
    const nombresValidos = empleadosEdit.every((r) => r.nombre.trim());
    if (!nombresValidos) {
      notify.error({ message: "Completa el nombre de todos los empleados", placement: "bottomRight" });
      return;
    }
    setSavingEmpleadosCard(true);
    try {
      const currentIds = new Set(empleadosEdit.filter((r) => r.id).map((r) => r.id));

      // Eliminar los que se quitaron de la lista
      const toDelete = [...originalEmpleadoIds.current].filter((id) => !currentIds.has(id));
      await Promise.all(toDelete.map((id) => dispatch(actionDesvinculacionEmpleadoDelete(id))));

      // Crear o actualizar cada empleado
      await Promise.all(empleadosEdit.map((r) => {
        const nombre = r.nombre.trim();
        return r.id
          ? dispatch(actionDesvinculacionEmpleadoUpdate(r.id, nombre))
          : dispatch(actionDesvinculacionEmpleadoCreate(detalle.id, nombre));
      }));

      await reloadDetalle();
      setEditingEmpleadosCard(false);
      notify.success({ message: "Empleados actualizados", placement: "bottomRight", duration: 2 });
    } catch (error) {
      notify.error({
        message: "No se pudieron guardar los empleados",
        description: error?.response?.data?.detail,
        placement: "bottomRight",
      });
    } finally {
      setSavingEmpleadosCard(false);
    }
  };

  const empleadosList = useMemo(() => {
    const arr = Array.isArray(detalle?.empleados) ? detalle.empleados : [];
    return arr.map((e) => String(e?.nombre_completo ?? e?.nombre_empleado ?? "").trim()).filter(Boolean);
  }, [detalle]);

  const esDirecto = useMemo(() => {
    const n = detalle?.nombre_corresponsal;
    const c = detalle?.correo_corresponsal;
    const t = detalle?.celular_corresponsal;
    return !(
      (n != null && String(n).trim() !== "") ||
      (c != null && String(c).trim() !== "") ||
      (t != null && String(t).trim() !== "")
    );
  }, [detalle]);

  const documentosActivos = useMemo(() => {
    const items = detalle?.documentos?.items;
    if (!Array.isArray(items)) return [];
    return items.filter((d) => Number(d?.active) === 1)
      .sort((a, b) => String(b?.created_at || "").localeCompare(String(a?.created_at || "")));
  }, [detalle]);

  const primaryDoc = documentosActivos[0] || null;
  const primaryPreviewUrl = useMemo(() => {
    const p0 = primaryDoc?.previews?.[0]?.url;
    return p0 ? `${API_BASE}${p0}` : null;
  }, [primaryDoc]);

  // Modalidad: preferir nombre del catálogo (JOIN), luego fallback a mapa viejo
  const modalidadLabel =
    detalle?.nombre_modalidad_baja ||
    (detalle?.via_tramite ? (MODALIDAD_LABEL[detalle.via_tramite] || detalle.via_tramite) : null);

  // Lugar: preferir nombre del catálogo (JOIN), luego varchar lugar_ejecucion, luego fallback viejo
  const lugarLabel =
    (() => {
      const nombre = detalle?.nombre_lugar_ejecucion;
      const texto = detalle?.lugar_ejecucion;
      const origen = detalle?.origen_privado;
      // Si el lugar es "Otro", mostrar el texto libre
      if (nombre && nombre.toLowerCase() === "otro" && texto && texto !== nombre) return texto;
      if (nombre) return nombre;
      if (texto) return LUGAR_LABEL[texto] || texto;
      if (origen) return LUGAR_LABEL[origen] || origen;
      return null;
    })();

  const esPrivada = detalle?.id_modalidad_baja === 1 || detalle?.via_tramite === "privado";

  /* ── documento handlers ── */
  const reloadDetalle = async () => {
    try {
      const data = await dispatch(actionDesvinculacionGetById(idExpediente));
      if (data) setDetalle(data);
    } catch {}
  };

  const handleOpenDocModal = () => {
    setDocFileList([]);
    setDocModalOpen(true);
  };

  const handleCloseDocModal = () => {
    setDocModalOpen(false);
    setDocFileList([]);
  };

  const handleDocSubmit = async () => {
    if (!docFileList.length) {
      notify.error({ message: "Archivo requerido", description: "Selecciona un archivo para continuar." });
      return;
    }
    try {
      setDocUploading(true);
      await dispatch(actionDesvinculacionDocumentoCreate(detalle.id, docFileList[0]));
      notify.success({ message: "Documento guardado", description: "El documento se adjuntó correctamente." });
      setDocModalOpen(false);
      setDocFileList([]);
      await reloadDetalle();
    } catch {
      notify.error({ message: "Error al guardar", description: "No se pudo guardar el documento." });
    } finally {
      setDocUploading(false);
    }
  };

  const handleDeleteDoc = async (doc) => {
    try {
      await dispatch(actionDesvinculacionDocumentoDelete(doc.id));
      notify.success({ message: "Documento eliminado" });
      await reloadDetalle();
    } catch {
      notify.error({ message: "Error al eliminar", description: "No se pudo eliminar el documento." });
    }
  };

  const startEditDatosCard = () => {
    const idEstadoInicial = ciudadesByIdCatalogo[detalle.id_ciudad]?.id_estado
      ? Number(ciudadesByIdCatalogo[detalle.id_ciudad].id_estado)
      : null;
    setSelectedEstadoId(idEstadoInicial);
    setSelectedCiudadId(detalle.id_ciudad ? Number(detalle.id_ciudad) : null);
    const esOtro = lugarOtroId != null && Number(detalle.id_lugar_ejecucion) === Number(lugarOtroId);
    setEditingDatosForm({
      fecha_encargo: detalle.fecha_encargo ? dayjs(detalle.fecha_encargo) : null,
      fecha_solicitacion: detalle.fecha_solicitacion ? dayjs(detalle.fecha_solicitacion) : null,
      id_razon_social: detalle.id_razon_social ? Number(detalle.id_razon_social) : null,
      id_modalidad_baja: detalle.id_modalidad_baja ? Number(detalle.id_modalidad_baja) : null,
      id_lugar_ejecucion: detalle.id_lugar_ejecucion ? Number(detalle.id_lugar_ejecucion) : null,
      lugar_ejecucion_otro: esOtro ? detalle.lugar_ejecucion || "" : "",
      domicilio: detalle.domicilio || "",
      notas: detalle.notas || "",
    });
    setEditingDatosCard(true);
  };

  const handleCancelEditDatos = () => setEditingDatosCard(false);

  const handleSaveDatosCard = async () => {
    if (!selectedEstadoId || !selectedCiudadId) {
      notify.error({ message: "Selecciona Estado y Ciudad", placement: "bottomRight" });
      return;
    }
    if (!editingDatosForm.id_razon_social) {
      notify.error({ message: "Selecciona la razón social", placement: "bottomRight" });
      return;
    }
    if (!editingDatosForm.fecha_encargo || !editingDatosForm.fecha_solicitacion) {
      notify.error({ message: "Completa las fechas de solicitud y de baja", placement: "bottomRight" });
      return;
    }
    if (!editingDatosForm.id_lugar_ejecucion) {
      notify.error({ message: "Selecciona el lugar de ejecución", placement: "bottomRight" });
      return;
    }
    const esOtro = lugarOtroId != null && Number(editingDatosForm.id_lugar_ejecucion) === Number(lugarOtroId);
    if (esOtro && !(editingDatosForm.lugar_ejecucion_otro || "").trim()) {
      notify.error({ message: "Especifica el lugar de ejecución", placement: "bottomRight" });
      return;
    }

    // La columna lugar_ejecucion (texto) es NOT NULL en la BD: siempre debe
    // llevar una etiqueta legible, ya sea la del catálogo o el texto libre
    // capturado cuando el lugar es "Otro".
    const lugarLabelPorId = Object.fromEntries(
      lugarEjecucionOptions
        .filter((o) => (o.label || "").toLowerCase() !== "otro")
        .map((o) => [o.value, o.label])
    );
    const lugarFinalLabel = esOtro
      ? editingDatosForm.lugar_ejecucion_otro.trim()
      : lugarLabelPorId[editingDatosForm.id_lugar_ejecucion] || null;

    if (!lugarFinalLabel) {
      notify.error({ message: "Selecciona el lugar de ejecución", placement: "bottomRight" });
      return;
    }

    setSavingDatosCard(true);
    try {
      const payload = {
        id_ciudad: Number(selectedCiudadId),
        id_razon_social: Number(editingDatosForm.id_razon_social),
        fecha_encargo: editingDatosForm.fecha_encargo.format("YYYY-MM-DD"),
        fecha_solicitacion: editingDatosForm.fecha_solicitacion.format("YYYY-MM-DD HH:mm:ss"),
        id_modalidad_baja: editingDatosForm.id_modalidad_baja ? Number(editingDatosForm.id_modalidad_baja) : null,
        id_lugar_ejecucion: Number(editingDatosForm.id_lugar_ejecucion),
        lugar_ejecucion: lugarFinalLabel,
        domicilio: editingDatosForm.domicilio ? editingDatosForm.domicilio.trim() : null,
        notas: editingDatosForm.notas ? editingDatosForm.notas.trim() : null,
      };
      await dispatch(actionDesvinculacionUpdateById(detalle.id, payload));
      await reloadDetalle();
      setEditingDatosCard(false);
      notify.success({ message: "Desvinculación actualizada", placement: "bottomRight" });
    } catch (error) {
      notify.error({
        message: "No se pudo guardar",
        description: error?.message,
        placement: "bottomRight",
      });
    } finally {
      setSavingDatosCard(false);
    }
  };

  const handleDelete = () => {
    if (!detalle?.id) {
      notify.error({ message: "No se puede eliminar", description: "ID no encontrado." });
      return;
    }
    Modal.confirm({
      title: "Eliminar desvinculación",
      content: "Esta acción elimina la desvinculación y su información relacionada.",
      okText: "Eliminar", okType: "danger", cancelText: "Cancelar", centered: true,
      okButtonProps: { loading: deletingExp, disabled: deletingExp },
      onOk: async () => {
        try {
          setDeletingExp(true);
          await dispatch(actionDesvinculacionDeleteById(detalle.id, () => {
            notify.success({ message: "Desvinculación eliminada" });
            navigate("/materias/laboral/desvinculaciones");
          }));
        } catch (error) {
          notify.error({ message: "Error al eliminar", description: error?.message });
        } finally {
          setDeletingExp(false);
        }
      },
    });
  };

  /* ── loading state ── */
  if (loadingDetalle || !detalle) {
    return (
      <main className="laboral-exp-main">
        <div className="laboral-exp-content">
          <Card className="laboral-exp-summary-card p-2">
            <Text>{loadingDetalle ? "Cargando desvinculación..." : "Sin datos"}</Text>
          </Card>
        </div>
      </main>
    );
  }

  const workerName = (detalle.nombre_solicitante || detalle.nombre_trabajador || "—").toUpperCase();
  const companyName = (detalle.nombre_empresa || "—").toUpperCase();
  const caseTitle = `${workerName} — ${companyName}`;

  const STATUS_COLOR_TO_KIND = { success: "green", warning: "yellow", error: "red", default: "gray" };
  const estatusPill = { label: status?.label?.toUpperCase() || "—", kind: STATUS_COLOR_TO_KIND[status?.color] || "gray" };

const TABS = [
    { key: "1", label: "Principal", icon: <i className="fa-solid fa-file" /> },
    { key: "4", label: "Resultado", icon: <i className="fa-solid fa-trophy" /> },
    { key: "2", label: "Documentos", icon: <i className="fa-solid fa-folder-open" /> },
  ];

  /* ─────────────────────────── render ─────────────────────────── */
  return (
    <main className="laboral-exp-main">
      <div className="laboral-exp-content">

        {/* ── Header card ── */}
        <section className="laboral-exp-section">
          <Card className="laboral-exp-summary-card p-2">
            <Row align="middle" gutter={[16, 16]} justify="space-between" wrap>
              <Col flex="auto">
                <Space direction="vertical" size={8}>
                  <div className="laboral-exp-summary-title">
                    <Space align="center" wrap>
                      <Title level={4} style={{ margin: 0 }}>{caseTitle}</Title>
                      <DataBadge
                        color={STATUS_PILL_HEX[estatusPill.kind] || estatusPill.kind}
                        className="d-inline-flex align-items-center gap-1 status-pill-badge"
                      >
                        {estatusPill.label}
                      </DataBadge>
                    </Space>
                    <Text type="secondary" className="laboral-exp-subtitle">
                      Núm. de expediente: {detalle.code || "Sin número"}
                    </Text>
                  </div>

                  <Space size="large" wrap className="laboral-exp-summary-meta">
                    <Space className="laboral-exp-meta-item">
                      <i className="fa-solid fa-location-dot" />
                      <Text>
                        Creado: {ciudadName}
                        {estadoName !== "—" ? `, ${estadoName}` : ""}
                      </Text>
                    </Space>
                    <Space className="laboral-exp-meta-item">
                      <i className="fa-solid fa-calendar-days" />
                      <Text>Creado: {fmtDate(detalle.created_at)}</Text>
                    </Space>
                    <Space className="laboral-exp-meta-item">
                      <i className="fa-solid fa-address-card" />
                      <Text>Creado por: {toTitleCase(detalle.nombre_usuario)}</Text>
                    </Space>
                  </Space>
                </Space>
              </Col>

              <Col flex="none">
                <Space>
                  <Button
                    danger type="primary"
                    icon={<i className="fa-solid fa-trash" />}
                    style={{ borderRadius: 8 }}
                    onClick={handleDelete}
                    loading={deletingExp}
                    disabled={deletingExp}
                  >
                    Eliminar
                  </Button>
                </Space>
              </Col>
            </Row>

            {/* Navigation tabs */}
            <div className="laboral-exp-header-tabs">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`laboral-exp-tab-btn ${activeTab === t.key ? "laboral-exp-tab-btn-active" : ""}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>
          </Card>
        </section>

        {/* ── Tab 1: Principal ── */}
        {activeTab === "1" && (
          <section className="laboral-exp-section">
            <Row gutter={[24, 24]} align="stretch">

              {/* Datos de la desvinculación */}
              <Col xs={24} lg={14}>
                <IconCard
                  icon={<ScissorOutlined />}
                  iconBg="#eff6ff"
                  iconColor="#2563eb"
                  title="Datos de la desvinculación"
                  extra={
                    !editingDatosCard && (
                      <Button
                        type="default"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={startEditDatosCard}
                        style={{ fontWeight: 600, borderRadius: 6 }}
                      >
                        Editar
                      </Button>
                    )
                  }
                >
                  {editingDatosCard ? (
                    <div className="laboral-exp-edit-form">
                      <div className="laboral-exp-edit-grid">
                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Estado</span>
                          <Select
                            style={{ width: "100%" }}
                            showSearch
                            optionFilterProp="label"
                            placeholder="Selecciona un estado"
                            options={estadosOptions}
                            value={selectedEstadoId}
                            onChange={(v) => {
                              setSelectedEstadoId(v ?? null);
                              setSelectedCiudadId(null);
                            }}
                          />
                        </div>

                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Ciudad</span>
                          <Select
                            style={{ width: "100%" }}
                            showSearch
                            optionFilterProp="label"
                            disabled={!selectedEstadoId}
                            placeholder={!selectedEstadoId ? "Selecciona un estado primero" : "Selecciona una ciudad"}
                            options={ciudadesOptionsCatalogo}
                            value={selectedCiudadId}
                            onChange={(v) => setSelectedCiudadId(v ?? null)}
                          />
                        </div>

                        <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                          <span className="laboral-exp-edit-label">Razón social</span>
                          <Select
                            style={{ width: "100%" }}
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            placeholder="Selecciona razón social"
                            options={razonesSocialesOptions}
                            value={editingDatosForm.id_razon_social}
                            onChange={(v) => setEditingDatosForm((f) => ({ ...f, id_razon_social: v ?? null }))}
                          />
                        </div>

                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Fecha de solicitud</span>
                          <ScrollDatePicker
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                            value={editingDatosForm.fecha_encargo}
                            onChange={(v) => setEditingDatosForm((f) => ({ ...f, fecha_encargo: v }))}
                          />
                        </div>

                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Fecha de la baja</span>
                          <ScrollDatePicker
                            style={{ width: "100%" }}
                            showTime={{ format: "HH:mm" }}
                            format="DD/MM/YYYY HH:mm"
                            value={editingDatosForm.fecha_solicitacion}
                            onChange={(v) => setEditingDatosForm((f) => ({ ...f, fecha_solicitacion: v }))}
                          />
                        </div>

                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Modalidad de baja</span>
                          <Select
                            style={{ width: "100%" }}
                            allowClear
                            placeholder="Selecciona"
                            options={modalidadBajaOptions}
                            value={editingDatosForm.id_modalidad_baja}
                            onChange={(v) => setEditingDatosForm((f) => ({ ...f, id_modalidad_baja: v ?? null }))}
                          />
                        </div>

                        <div className="laboral-exp-edit-field">
                          <span className="laboral-exp-edit-label">Lugar de ejecución</span>
                          <Select
                            style={{ width: "100%" }}
                            placeholder="Selecciona"
                            options={lugarEjecucionOptions}
                            value={editingDatosForm.id_lugar_ejecucion}
                            onChange={(v) => setEditingDatosForm((f) => ({ ...f, id_lugar_ejecucion: v ?? null }))}
                          />
                        </div>

                        {lugarOtroId != null && Number(editingDatosForm.id_lugar_ejecucion) === Number(lugarOtroId) && (
                          <div className="laboral-exp-edit-field">
                            <span className="laboral-exp-edit-label">Especifica el lugar</span>
                            <Input
                              value={editingDatosForm.lugar_ejecucion_otro}
                              onChange={(e) => setEditingDatosForm((f) => ({ ...f, lugar_ejecucion_otro: e.target.value }))}
                            />
                          </div>
                        )}

                        <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                          <span className="laboral-exp-edit-label">Domicilio de ejecución</span>
                          <Input
                            value={editingDatosForm.domicilio}
                            onChange={(e) => setEditingDatosForm((f) => ({ ...f, domicilio: e.target.value }))}
                          />
                        </div>

                        <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                          <span className="laboral-exp-edit-label">Notas</span>
                          <Input.TextArea
                            rows={3}
                            value={editingDatosForm.notas}
                            onChange={(e) => setEditingDatosForm((f) => ({ ...f, notas: e.target.value }))}
                          />
                        </div>
                      </div>

                      <div className="laboral-exp-edit-actions">
                        <Button onClick={handleCancelEditDatos}>Cancelar</Button>
                        <Button type="primary" loading={savingDatosCard} onClick={handleSaveDatosCard}>
                          Guardar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px 28px" }}>
                      <FieldItem label="Empresa" value={toTitleCase(detalle.nombre_empresa)} />
                      <FieldItem label="Razón social" value={toTitleCase(detalle.nombre_razon_social)} />
                      <FieldItem label="Fecha de solicitud" value={fmtDate(detalle.fecha_encargo)} />
                      <FieldItem
                        label="Fecha de la baja"
                        value={fmtDateTime(detalle.fecha_solicitacion)}
                        valueColor={getFechaBajaColor(detalle.fecha_solicitacion)}
                      />
                      <FieldItem label="Ciudad" value={toTitleCase(ciudadName)} />
                      <FieldItem label="Estado" value={toTitleCase(estadoName)} />
                      <FieldItem
                        label="Modalidad de baja"
                        value={modalidadLabel || "—"}
                        isTag={!!modalidadLabel}
                        tagColor={esPrivada ? "red" : "blue"}
                      />
                      <FieldItem label="Lugar de ejecución" value={lugarLabel || "—"} />
                      {(detalle.domicilio || detalle.domicilio_ejecucion) && (
                        <FieldItem
                          label="Domicilio de ejecución"
                          value={safeText(detalle.domicilio || detalle.domicilio_ejecucion)}
                          span={2}
                        />
                      )}
                      {detalle.notas && (
                        <FieldItem label="Notas" value={safeText(detalle.notas)} span={2} />
                      )}
                    </div>
                  )}
                </IconCard>
              </Col>

              {/* Empleados */}
              <Col xs={24} lg={10}>
                <Card
                  style={{ borderRadius: 12, border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,.06)", height: "100%" }}
                  bodyStyle={{ padding: 24 }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 20 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ background: "#eff6ff", borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <TeamOutlined style={{ fontSize: 18, color: "#2563eb" }} />
                      </div>
                      <div>
                        <Text strong style={{ fontSize: 16, color: "#111827", display: "block" }}>Empleados</Text>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          {empleadosList.length
                            ? `${empleadosList.length} ${empleadosList.length === 1 ? "empleado registrado" : "empleados registrados"}`
                            : "Sin empleados registrados"}
                        </Text>
                      </div>
                    </div>
                    {!editingEmpleadosCard && (
                      <Button
                        type="default"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={startEditEmpleadosCard}
                        style={{ fontWeight: 600, borderRadius: 6 }}
                      >
                        Editar
                      </Button>
                    )}
                  </div>

                  {editingEmpleadosCard ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {empleadosEdit.map((row, idx) => (
                        <div key={row._key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Input
                            value={row.nombre}
                            onChange={(e) => updateEmpleadoRow(row._key, e.target.value)}
                            placeholder={`Empleado ${idx + 1}`}
                          />
                          <Button
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                            disabled={empleadosEdit.length <= 1}
                            onClick={() => removeEmpleadoRow(row._key)}
                          />
                        </div>
                      ))}

                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={addEmpleadoRow}
                        style={{ borderRadius: 8 }}
                      >
                        Agregar empleado
                      </Button>

                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
                        <Button onClick={handleCancelEditEmpleados} disabled={savingEmpleadosCard}>Cancelar</Button>
                        <Button type="primary" loading={savingEmpleadosCard} onClick={handleSaveEmpleados}>
                          Guardar
                        </Button>
                      </div>
                    </div>
                  ) : empleadosList.length ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {empleadosList.map((name, idx) => {
                        const initials = name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
                        const palette = ["#2563eb", "#7c3aed", "#0891b2", "#059669", "#d97706", "#dc2626", "#0f766e"];
                        const avatarBg = palette[idx % palette.length];
                        return (
                          <div
                            key={`principal-${name}-${idx}`}
                            style={{
                              display: "flex", alignItems: "center", gap: 14,
                              background: "#fff", borderRadius: 10,
                              border: "1px solid #e5e7eb",
                              padding: "12px 16px",
                              boxShadow: "0 1px 3px rgba(0,0,0,.04)",
                              transition: "box-shadow .15s, border-color .15s",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,.08)"; e.currentTarget.style.borderColor = "#93c5fd"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,.04)"; e.currentTarget.style.borderColor = "#e5e7eb"; }}
                          >
                            <div style={{
                              width: 40, height: 40, borderRadius: "50%",
                              background: avatarBg, color: "#fff",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 14, fontWeight: 700, flexShrink: 0,
                            }}>
                              {initials || <UserOutlined />}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {toTitleCase(name)}
                              </div>
                              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>Empleado</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{
                      textAlign: "center", padding: "40px 16px",
                      background: "#f9fafb", borderRadius: 10,
                      border: "1.5px dashed #d1d5db",
                    }}>
                      <TeamOutlined style={{ fontSize: 32, color: "#d1d5db", display: "block", margin: "0 auto 12px" }} />
                      <Text strong style={{ fontSize: 14, color: "#374151", display: "block", marginBottom: 4 }}>
                        Sin empleados registrados
                      </Text>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        Esta desvinculación no tiene empleados asociados.
                      </Text>
                    </div>
                  )}
                </Card>
              </Col>

            </Row>
          </section>
        )}

        {/* ── Tab 1: Asunto vinculado (Centro de Conciliación) ── */}
        {activeTab === "1" && (vinculoConciliacion || loadingVinculo) && (
          <section className="laboral-exp-section">
            <IconCard
              icon={<LinkOutlined />}
              iconBg="#f0f9ff"
              iconColor="#0369a1"
              title="Asunto vinculado"
            >
              {loadingVinculo ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#6b7280" }}>
                  <LoadingOutlined /> Cargando expediente vinculado...
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
                      {vinculoConciliacion.expediente
                        ? `Núm. ${vinculoConciliacion.expediente}`
                        : `Conciliación #${vinculoConciliacion.id}`}
                    </div>
                    {Array.isArray(vinculoConciliacion.trabajadores) && vinculoConciliacion.trabajadores.length > 0 && (
                      <div style={{ fontSize: 13, color: "#374151" }}>
                        <span style={{ color: "#9ca3af", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", marginRight: 6 }}>Trabajador</span>
                        {toTitleCase(
                          vinculoConciliacion.trabajadores.map((t) => t?.nombre_trabajador).filter(Boolean).join(", ")
                        )}
                      </div>
                    )}
                    {vinculoConciliacion.status_nombre && (
                      <div>
                        <span style={{ color: "#9ca3af", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", marginRight: 6 }}>Estado</span>
                        <Tag color={
                          normalize(vinculoConciliacion.status_nombre).includes("no concili") ? "error" :
                          normalize(vinculoConciliacion.status_nombre).includes("concili") ? "success" :
                          normalize(vinculoConciliacion.status_nombre).includes("proceso") ? "processing" :
                          "default"
                        }>
                          {vinculoConciliacion.status_nombre}
                        </Tag>
                      </div>
                    )}
                  </div>
                  <Button
                    type="primary"
                    ghost
                    icon={<ExportOutlined />}
                    style={{ borderRadius: 8, alignSelf: "center" }}
                    onClick={() => navigate(`/materias/laboral/centro-conciliacion/${vinculoConciliacion.id}`)}
                  >
                    Ver expediente
                  </Button>
                </div>
              )}
            </IconCard>
          </section>
        )}

        {/* ── Tab 2: Documentos ── */}
        {activeTab === "2" && (
          <section className="laboral-exp-section">
            <Card
              className="laboral-exp-detail-card-main"
              title={
                <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 0" }}>
                  <Text style={{ fontSize: 16, fontWeight: 700 }}>Documentos de la desvinculación</Text>
                  <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
                    Administra y visualiza los documentos adjuntos.
                  </Text>
                </div>
              }
              extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenDocModal}>
                  Agregar documento
                </Button>
              }
            >
              <div style={{ padding: "12px 24px 24px" }}>
                {documentosActivos.length === 0 ? (
                  <div style={{
                    textAlign: "center", padding: "48px 24px",
                    background: "#f9fafb", borderRadius: 12,
                    border: "1.5px dashed #d1d5db",
                  }}>
                    <FolderOpenOutlined style={{ fontSize: 36, color: "#d1d5db", display: "block", margin: "0 auto 12px" }} />
                    <Text strong style={{ fontSize: 14, color: "#374151", display: "block", marginBottom: 4 }}>
                      Sin documentos adjuntos
                    </Text>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      No hay documentos activos para esta desvinculación.
                    </Text>
                    <div style={{ marginTop: 16 }}>
                      <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenDocModal}>
                        Agregar documento
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Row gutter={[24, 32]} style={{ padding: "12px 0" }}>
                    {documentosActivos.map((doc) => {
                      const mainPreview = doc.previews?.[0]?.url ? `${API_BASE}${doc.previews[0].url}` : null;
                      const displayName = doc.filename
                        || (doc.path ? String(doc.path).split("/").pop() : null)
                        || doc.nombre
                        || "Documento";
                      return (
                        <Col key={doc.id} xs={24} sm={12} md={8} lg={8} xl={6}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 8 }}>
                            <div className="laboral-exp-pdf-preview-card">
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8 }}>
                                <Tooltip title={displayName} placement="top" mouseEnterDelay={0.2}>
                                  <Text style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, minWidth: 0 }}>
                                    {displayName}
                                  </Text>
                                </Tooltip>
                                <Popconfirm
                                  title="Eliminar documento"
                                  description="Esta acción no se puede deshacer. ¿Deseas continuar?"
                                  okText="Eliminar"
                                  cancelText="Cancelar"
                                  okButtonProps={{ danger: true, size: "small" }}
                                  onConfirm={() => handleDeleteDoc(doc)}
                                >
                                  <Button size="small" type="text" danger icon={<DeleteOutlined />} style={{ flexShrink: 0 }}>
                                    Eliminar
                                  </Button>
                                </Popconfirm>
                              </div>
                              <div className="laboral-exp-pdf-preview-inner">
                                {mainPreview ? (
                                  <Image.PreviewGroup>
                                    <Image
                                      className="laboral-exp-pdf-thumbnail"
                                      src={mainPreview}
                                      alt={displayName}
                                      preview={{
                                        maskClassName: "laboral-exp-pdf-mask",
                                        mask: <span className="laboral-exp-pdf-mask-text">Ver documento completo</span>,
                                      }}
                                    />
                                    {doc.previews?.slice(1).map((p) => (
                                      <Image
                                        key={`${doc.id}-page-${p.page ?? p.url}`}
                                        src={`${API_BASE}${p.url}`}
                                        alt={`Página ${p.page}`}
                                        style={{ display: "none" }}
                                      />
                                    ))}
                                  </Image.PreviewGroup>
                                ) : (
                                  <div style={{
                                    display: "flex", flexDirection: "column", alignItems: "center",
                                    justifyContent: "center", padding: "32px 16px", gap: 10,
                                    background: "#f8fafc", minHeight: 120,
                                  }}>
                                    <FileOutlined style={{ fontSize: 40, color: "#94a3b8" }} />
                                    <Text type="secondary" style={{ fontSize: 12, textAlign: "center", wordBreak: "break-all" }}>
                                      {displayName}
                                    </Text>
                                  </div>
                                )}
                              </div>
                            </div>
                            {doc.created_at && (
                              <Text type="secondary" style={{ fontSize: 12, textAlign: "center", display: "block", width: "100%" }}>
                                Cargado el {fmtDate(doc.created_at)}
                              </Text>
                            )}
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </div>
            </Card>

            {/* Modal agregar documento */}
            <Modal
              title="Agregar documento"
              open={docModalOpen}
              onCancel={handleCloseDocModal}
              onOk={handleDocSubmit}
              okText="Guardar documento"
              cancelText="Cancelar"
              confirmLoading={docUploading}
              cancelButtonProps={{ disabled: docUploading }}
              destroyOnClose
            >
              <div style={{ padding: "8px 0" }}>
                <Upload
                  multiple={false}
                  fileList={docFileList}
                  beforeUpload={(file) => {
                    setDocFileList([file]);
                    return false;
                  }}
                  onRemove={() => setDocFileList([])}
                >
                  <Button icon={<UploadOutlined />}>Seleccionar archivo</Button>
                </Upload>
                <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: "block" }}>
                  Se aceptan PDF, Word, Excel, PowerPoint, imágenes (PNG, JPG) y más.
                </Text>
              </div>
            </Modal>
          </section>
        )}

        {/* ── Tab 4: Resultado ── */}
        {activeTab === "4" && (
          <section className="laboral-exp-section">
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              {/* ── Selector de resultado ── */}
              <Card
                style={{ borderRadius: 12, border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}
                bodyStyle={{ padding: 24 }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                  <div style={{ background: "#f0fdf4", borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <TrophyOutlined style={{ fontSize: 18, color: "#16a34a" }} />
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>Resultado de la desvinculación</span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 320px", maxWidth: 480 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "#9ca3af", marginBottom: 8, letterSpacing: "0.04em" }}>
                      Selecciona el resultado
                    </div>
                    <Select
                      value={resultadoBaja}
                      onChange={handleResultadoChange}
                      options={resultadosOptions}
                      placeholder="Sin resultado registrado"
                      allowClear
                      loading={resultadoSaving}
                      style={{ width: "100%" }}
                      size="large"
                    />
                  </div>

                </div>
              </Card>

              {/* ── Confirmación visual de espera de citatorio ── */}
              {esResultadoCitatorio(resultadosOptions.find(o => o.value === resultadoBaja)?.label ?? "") && (
                <Card
                  style={{ borderRadius: 12, border: "1.5px solid #fcd34d", background: "#fffbeb", boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}
                  bodyStyle={{ padding: 20 }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ background: "#fef9c3", borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <ClockCircleOutlined style={{ fontSize: 18, color: "#d97706" }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#92400e" }}>
                          En espera de citatorio prejudicial
                        </div>
                        <div style={{ fontSize: 12, color: "#b45309", marginTop: 2 }}>
                          Resultado registrado correctamente
                        </div>
                      </div>
                    </div>
                    {!vinculoConciliacion && (
                      <Button
                        type="default"
                        icon={<LinkOutlined />}
                        style={{ borderColor: "#d97706", color: "#d97706", borderRadius: 8 }}
                        onClick={() => setShowLinkDialog(true)}
                      >
                        Vincular expediente CC
                      </Button>
                    )}
                    {vinculoConciliacion && (
                      <Button
                        type="default"
                        ghost
                        icon={<ExportOutlined />}
                        style={{ borderColor: "#d97706", color: "#d97706", borderRadius: 8 }}
                        onClick={() => navigate(`/materias/laboral/centro-conciliacion/${vinculoConciliacion.id}`)}
                      >
                        Ver expediente vinculado
                      </Button>
                    )}
                  </div>
                </Card>
              )}

              {/* ── Tabla de pagos del convenio (solo si resultado con convenio) ── */}
              {esResultadoConvenio(resultadosOptions.find(o => o.value === resultadoBaja)?.label ?? "") && (
                <Card
                  style={{ borderRadius: 12, border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}
                  bodyStyle={{ padding: 24 }}
                >
                  {/* Header del card con botones según modo */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ background: "#eff6ff", borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <BankOutlined style={{ fontSize: 18, color: "#2563eb" }} />
                      </div>
                      <Text strong style={{ fontSize: 16, color: "#111827" }}>Pagos del convenio</Text>
                    </div>

                    <Space>
                      {pagoEditMode ? (
                        <>
                          <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={addPago}
                            style={{ borderRadius: 8 }}
                          >
                            Agregar pago
                          </Button>
                          <Button
                            type="primary"
                            loading={pagosSaving}
                            onClick={handleSavePagos}
                            style={{ borderRadius: 8 }}
                          >
                            Guardar
                          </Button>
                        </>
                      ) : (
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => setPagoEditMode(true)}
                          style={{ borderRadius: 8 }}
                        >
                          Editar
                        </Button>
                      )}
                    </Space>
                  </div>

                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: "#f8fafc" }}>
                          {["#", "TIPO DE PAGO", "MONTO (MXN)", "FECHA", "MÉTODO DE PAGO", ...(pagoEditMode ? ["ACCIONES"] : [])].map((h, i, arr) => (
                            <th key={h} style={{
                              padding: "10px 12px",
                              textAlign: (i === 0 || i === arr.length - 1) && pagoEditMode ? "center" : "left",
                              fontSize: 11, fontWeight: 700, color: "#1677ff",
                              textTransform: "uppercase", letterSpacing: "0.05em",
                              borderBottom: "2px solid #e5e7eb", whiteSpace: "nowrap",
                            }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pagosConvenio.length === 0 && (
                          <tr>
                            <td colSpan={pagoEditMode ? 6 : 5} style={{ textAlign: "center", padding: "28px 12px", color: "#9ca3af", fontSize: 13 }}>
                              {pagoEditMode
                                ? <>No hay pagos. Presiona <strong>"Agregar pago"</strong> para comenzar.</>
                                : "No hay pagos registrados."}
                            </td>
                          </tr>
                        )}
                        {pagosConvenio.map((pago, index) => (
                          <tr key={pago._key} style={{ borderBottom: "1px solid #f3f4f6" }}>
                            <td style={{ textAlign: "center", padding: "10px 12px", fontWeight: 600, color: "#374151" }}>
                              {index + 1}
                            </td>

                            {pagoEditMode ? (
                              <>
                                <td style={{ padding: "8px 8px" }}>
                                  <Select
                                    value={pago.tipo_pago}
                                    onChange={(v) => handleTipoPagoChange(pago._key, v)}
                                    options={TIPO_PAGO_OPTIONS}
                                    placeholder="Tipo"
                                    style={{ width: "100%", minWidth: 130 }}
                                  />
                                </td>
                                <td style={{ padding: "8px 8px" }}>
                                  <InputNumber
                                    value={pago.monto}
                                    onChange={(v) => updatePago(pago._key, "monto", v)}
                                    min={0} step={0.01}
                                    placeholder="Ej. 20,000.00"
                                    controls={false}
                                    style={{ width: "100%" }}
                                    formatter={(v) => {
                                      if (v === undefined || v === null || v === "") return "";
                                      const [int, dec] = v.toString().split(".");
                                      const intC = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                                      return dec !== undefined ? `$ ${intC}.${dec}` : `$ ${intC}`;
                                    }}
                                    parser={(v) => (v ? v.replace(/\$/g, "").replace(/\s/g, "").replace(/,/g, "") : "")}
                                  />
                                </td>
                                <td style={{ padding: "8px 8px" }}>
                                  <Tooltip title={pago.tipo_pago === "inmediato" ? "Fecha tomada de la fecha de la baja" : null}>
                                    <ScrollDatePicker
                                      value={pago.fecha}
                                      onChange={(v) => updatePago(pago._key, "fecha", v)}
                                      showTime={{ format: "HH:mm" }}
                                      format="DD/MM/YYYY HH:mm"
                                      placeholder="DD/MM/YYYY HH:mm"
                                      style={{ width: "100%", minWidth: 180 }}
                                      disabled={pago.tipo_pago === "inmediato"}
                                    />
                                  </Tooltip>
                                </td>
                                <td style={{ padding: "8px 8px" }}>
                                  <Select
                                    value={pago.forma_pago}
                                    onChange={(v) => updatePago(pago._key, "forma_pago", v)}
                                    options={METODO_PAGO_OPTIONS}
                                    placeholder="Método de pago"
                                    style={{ width: "100%", minWidth: 160 }}
                                  />
                                </td>
                                <td style={{ textAlign: "center", padding: "8px 8px" }}>
                                  <Button danger type="text" icon={<DeleteOutlined />} onClick={() => removePago(pago._key)} />
                                </td>
                              </>
                            ) : (
                              <>
                                <td style={{ padding: "10px 12px" }}>
                                  <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                                    {TIPO_PAGO_OPTIONS.find((o) => o.value === pago.tipo_pago)?.label || "—"}
                                  </span>
                                </td>
                                <td style={{ padding: "10px 12px" }}>
                                  <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                                    {pago.monto != null
                                      ? `$ ${Number(pago.monto).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                      : "—"}
                                  </span>
                                </td>
                                <td style={{ padding: "10px 12px" }}>
                                  <span style={{ fontSize: 13, color: "#374151" }}>
                                    {pago.fecha ? dayjs(pago.fecha).format("DD/MM/YYYY HH:mm") : "—"}
                                  </span>
                                </td>
                                <td style={{ padding: "10px 12px" }}>
                                  <span style={{ fontSize: 13, color: "#374151" }}>
                                    {METODO_PAGO_OPTIONS.find((o) => o.value === pago.forma_pago)?.label || "—"}
                                  </span>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {/* ── Sin resultado seleccionado ── */}
              {!resultadoBaja && (
                <div style={{
                  textAlign: "center", padding: "40px 24px",
                  background: "#f9fafb", borderRadius: 12,
                  border: "1px dashed #d1d5db",
                  color: "#9ca3af", fontSize: 14,
                }}>
                  <TrophyOutlined style={{ fontSize: 32, marginBottom: 10, display: "block", margin: "0 auto 10px" }} />
                  Selecciona un resultado para ver las acciones disponibles.
                </div>
              )}

            </div>
          </section>
        )}

      </div>

      {/* ── Diálogo: crear expediente en Centro de Conciliación ── */}
      <Modal
        open={showLinkDialog}
        title={
          <Space>
            <LinkOutlined style={{ color: "#2563eb" }} />
            Crear expediente en Centro de Conciliación
          </Space>
        }
        onCancel={() => setShowLinkDialog(false)}
        footer={null}
        centered
        width={480}
      >
        <div style={{ padding: "8px 0 16px" }}>
          <p style={{ fontSize: 14, color: "#374151", marginBottom: 24, lineHeight: 1.6 }}>
            ¿Deseas crear un expediente en el Centro de Conciliación vinculado a esta baja?
            Los datos principales se transferirán automáticamente.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button onClick={() => setShowLinkDialog(false)}>
              Ahora no
            </Button>
            <Button type="primary" icon={<LinkOutlined />} onClick={handleCreateLinkedExpediente}>
              Crear expediente
            </Button>
          </div>
        </div>
      </Modal>

    </main>
  );
}
